import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import SmsIcon from "@mui/icons-material/Sms";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import CustomerWithoutRequestLeadApi from "../../../../../infrastructure/api/customerWithoutRequestLead";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import PartnerReferralFilterSelect from "../../solarent-operator/shared/PartnerReferralFilterSelect";

const CONTACT_STATUS_OPTIONS = [
  { value: 0, label: "در انتظار تماس" },
  { value: 1, label: "تماس گرفته شد" },
  { value: 2, label: "قصد ثبت دارد" },
  { value: 3, label: "عدم تمایل" },
  { value: 4, label: "پاسخ نداد" },
];

const CALL_RESULT_PRESETS = [
  { value: "تماس برقرار شد", contactStatus: "1" },
  { value: "قصد ثبت دارد", contactStatus: "2" },
  { value: "عدم تمایل", contactStatus: "3" },
  { value: "پاسخ نداد", contactStatus: "4" },
];

const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return null;
    return u.id ?? u.userId ?? u.UserId ?? null;
  } catch {
    return null;
  }
};

const CustomerWithoutRequestLeadsPage = ({
  api = CustomerWithoutRequestLeadApi,
  operatorMode = false,
}) => {
  const operatorFilterInitDone = useRef(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [filterTick, setFilterTick] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [operators, setOperators] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    contactStatus: "",
    needsFollowUp: "",
    assignedOperatorUserId: "",
    partnerReferralLevel1PartnerId: "",
    search: "",
    registeredFrom: null,
    registeredTo: null,
  });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgePreview, setNudgePreview] = useState(null);
  const [nudgeForm, setNudgeForm] = useState({
    profileCompletedOnly: true,
    maxCount: 700,
    customMessage: "",
  });
  const [assignedOperatorUserId, setAssignedOperatorUserId] = useState("");
  const [followUpForm, setFollowUpForm] = useState({
    callResult: "",
    description: "",
    noRequestReasonCategory: "",
    noRequestReasonNotes: "",
    contactStatus: "",
    needsFollowUp: true,
    nextFollowUpAt: null,
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!operatorMode || operatorFilterInitDone.current) return;
    const uid = getCurrentUserId();
    if (uid == null) return;
    operatorFilterInitDone.current = true;
    setFilters((f) => ({ ...f, assignedOperatorUserId: String(uid) }));
    setFilterTick((x) => x + 1);
  }, [operatorMode]);

  useEffect(() => {
    Promise.all([api.getReasonOptions(), api.getOperators()])
      .then(([reasonRes, operatorRes]) => {
        if (reasonRes?.isSuccess) setReasons(reasonRes.data || []);
        if (operatorRes?.isSuccess) setOperators(operatorRes.data || []);
      })
      .catch(() => {});
  }, [api]);

  const loadList = useCallback(async () => {
    const f = filtersRef.current;
    const params = {
      page: page + 1,
      pageSize,
    };
    if (f.contactStatus !== "") params.contactStatus = Number(f.contactStatus);
    if (f.needsFollowUp === "1") params.needsFollowUp = true;
    if (f.assignedOperatorUserId) params.assignedOperatorUserId = f.assignedOperatorUserId;
    if (f.partnerReferralLevel1PartnerId) {
      params.partnerReferralLevel1PartnerId = Number(f.partnerReferralLevel1PartnerId);
    }
    if (f.search) params.search = f.search;
    if (f.registeredFrom) params.registeredFrom = f.registeredFrom;
    if (f.registeredTo) params.registeredTo = f.registeredTo;

    const statsParams = {};
    if (f.assignedOperatorUserId) {
      statsParams.assignedOperatorUserId = f.assignedOperatorUserId;
    }
    if (f.partnerReferralLevel1PartnerId) {
      statsParams.partnerReferralLevel1PartnerId = Number(
        f.partnerReferralLevel1PartnerId
      );
    }

    try {
      setLoading(true);
      const [res, statsRes] = await Promise.all([
        api.getPagedList(params),
        api.getStats(statsParams).catch(() => null),
      ]);
      if (res?.isSuccess && res.data) {
        setRows(res.data.items || res.data.Items || []);
        setTotal(Number(res.data.total ?? res.data.Total) || 0);
      } else {
        toast.error(res?.message || res?.Message || "خطا در دریافت لیست");
      }
      if (statsRes?.isSuccess && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.Message ||
        e?.message ||
        "خطا در دریافت لیست";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize]);

  useEffect(() => {
    loadList();
  }, [loadList, filterTick]);

  const saveAssignedOperator = async () => {
    if (!detail?.userId) return;
    try {
      const res = await api.updateLead({
        userId: detail.userId,
        assignedOperatorUserId: assignedOperatorUserId
          ? Number(assignedOperatorUserId)
          : null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در ذخیره کارشناس");
        return;
      }
      toast.success("کارشناس ارجاع‌شده ذخیره شد");
      const detailRes = await api.getDetail(detail.userId);
      if (detailRes?.isSuccess) setDetail(detailRes.data);
      setFilterTick((x) => x + 1);
    } catch {
      toast.error("خطا در ذخیره کارشناس");
    }
  };

  const openDetail = async (userId) => {
    try {
      const res = await api.getDetail(userId);
      if (!res?.isSuccess) {
        toast.error(res?.message || "خطا در دریافت جزئیات");
        return;
      }
      const data = res.data;
      setDetail(data);
      setAssignedOperatorUserId(
        data.assignedOperatorUserId != null
          ? String(data.assignedOperatorUserId)
          : ""
      );
      setFollowUpForm({
        callResult: "",
        description: "",
        noRequestReasonCategory: data.noRequestReasonCategory || "",
        noRequestReasonNotes: data.noRequestReasonNotes || "",
        contactStatus:
          data.contactStatus != null ? String(data.contactStatus) : "1",
        needsFollowUp: true,
        nextFollowUpAt: null,
      });
      setDetailOpen(true);
    } catch {
      toast.error("خطا در دریافت جزئیات");
    }
  };

  const submitFollowUp = async () => {
    if (!detail?.userId || !followUpForm.description?.trim()) {
      toast.error("ثبت توضیحات تماس الزامی است");
      return;
    }
    const contactStatus =
      followUpForm.contactStatus !== ""
        ? Number(followUpForm.contactStatus)
        : null;
    const isNotInterested = contactStatus === 3;
    try {
      const res = await api.addFollowUp({
        userId: detail.userId,
        callResult: followUpForm.callResult || (isNotInterested ? "عدم تمایل" : ""),
        description: followUpForm.description,
        noRequestReasonCategory: followUpForm.noRequestReasonCategory || null,
        noRequestReasonNotes: followUpForm.noRequestReasonNotes || null,
        contactStatus,
        needsFollowUp: isNotInterested ? false : followUpForm.needsFollowUp,
        nextFollowUpAt: isNotInterested ? null : followUpForm.nextFollowUpAt,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در ثبت");
        return;
      }
      toast.success(
        isNotInterested
          ? "ثبت شد؛ متقاضی از صف کار حذف شد"
          : res?.message || "پیگیری ثبت شد"
      );
      setDetailOpen(false);
      setFilterTick((x) => x + 1);
    } catch {
      toast.error("خطا در ثبت پیگیری");
    }
  };

  const runNudgeSms = async ({ dryRun, confirmSend }) => {
    try {
      setNudgeLoading(true);
      const res = await api.sendSubmitRequestNudgeSms({
        dryRun,
        confirmSend: Boolean(confirmSend),
        profileCompletedOnly: nudgeForm.profileCompletedOnly,
        maxCount: Number(nudgeForm.maxCount) || 50,
        customMessage: nudgeForm.customMessage?.trim() || null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در ارسال پیامک");
        return;
      }
      setNudgePreview(res?.data || null);
      toast.success(res?.message || (dryRun ? "پیش‌نمایش آماده شد" : "ارسال انجام شد"));
      if (!dryRun) {
        setNudgeOpen(false);
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setNudgeLoading(false);
    }
  };

  const headCells = useMemo(
    () => [
      { id: "fullName", label: "نام" },
      { id: "mobile", label: "موبایل" },
      { id: "partnerReferralDisplayName", label: "معرف (سطح یک)" },
      { id: "registeredAt", label: "تاریخ ثبت‌نام", type: "persiandatetime" },
      { id: "profileStatusTitle", label: "وضعیت پروفایل" },
      {
        id: "contactStatusTitle",
        label: "وضعیت تماس",
        type: "chip",
        model: { colorField: "contactChipColor" },
      },
      { id: "noRequestReasonDisplay", label: "دلیل عدم ثبت" },
      { id: "assignedOperatorName", label: "کارشناس ارجاع" },
      { id: "lastContactedByOperatorName", label: "کارشناس تماس" },
      { id: "nextFollowUpAt", label: "پیگیری بعدی", type: "persiandatetime" },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      (rows || []).map((row, index) => ({
        ...row,
        row_index: page * pageSize + index + 1,
        fullName: row.fullName || row.FullName || "—",
        mobile: row.mobile || row.Mobile || "—",
        partnerReferralDisplayName:
          row.partnerReferralDisplayName ||
          row.PartnerReferralDisplayName ||
          "—",
        registeredAt: row.registeredAt || row.RegisteredAt || null,
        profileStatusTitle:
          row.profileStatusTitle || row.ProfileStatusTitle || "—",
        contactStatusTitle:
          row.contactStatusTitle ||
          row.ContactStatusTitle ||
          "در انتظار تماس",
        contactChipColor: row.needsFollowUp || row.NeedsFollowUp
          ? "warning"
          : "gray",
        noRequestReasonDisplay:
          row.noRequestReasonCategoryTitle ||
          row.NoRequestReasonCategoryTitle ||
          row.noRequestReasonNotes ||
          row.NoRequestReasonNotes ||
          "—",
        assignedOperatorName:
          row.assignedOperatorName || row.AssignedOperatorName || "—",
        lastContactedByOperatorName:
          row.lastContactedByOperatorName ||
          row.LastContactedByOperatorName ||
          "—",
        nextFollowUpAt: row.nextFollowUpAt || row.NextFollowUpAt || null,
        needsFollowUp: Boolean(row.needsFollowUp || row.NeedsFollowUp),
      })),
    [rows, page, pageSize]
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <PhoneInTalkIcon fontSize="small" />,
        color: "primary",
        tooltip: "تماس / پیگیری",
        onClick: (row) => openDetail(row.userId || row.UserId),
      },
    ],
    // openDetail is stable enough for this page; recreate when api changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api]
  );

  const serverPagination = useMemo(
    () => ({
      totalCount: total,
      page,
      rowsPerPage: pageSize,
      onPageChange: (newPage) => setPage(newPage),
      onRowsPerPageChange: (next) => {
        setPageSize(next);
        setPage(0);
      },
    }),
    [total, page, pageSize]
  );

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent />
      <Typography variant="h5" gutterBottom sx={{ mt: 1 }}>
        متقاضیان بدون درخواست
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        کاربرانی که وارد سامانه شده‌اند اما هنوز درخواست نیروگاه ثبت نکرده‌اند.
        پس از تماس، دلیل عدم ثبت و نتیجه پیگیری را ثبت کنید. موارد «عدم تمایل»
        از صف کار حذف می‌شوند و با فیلتر همان وضعیت برای آمار قابل مشاهده‌اند.
      </Typography>
      {stats ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          {[
            {
              label: "تعداد کل",
              value: stats.totalWithoutRequest ?? stats.TotalWithoutRequest ?? 0,
              color: "primary.main",
            },
            {
              label: "پروفایل تکمیل‌شده",
              value:
                stats.profileCompletedCount ?? stats.ProfileCompletedCount ?? 0,
              color: "success.main",
            },
            {
              label: "پروفایل ناقص",
              value:
                stats.profileIncompleteCount ??
                stats.ProfileIncompleteCount ??
                0,
              color: "warning.main",
            },
            {
              label: "صف کار فعال",
              value:
                stats.activeWorkQueueCount ?? stats.ActiveWorkQueueCount ?? 0,
              color: "info.main",
            },
            {
              label: "عدم تمایل",
              value: stats.notInterested ?? stats.NotInterested ?? 0,
              color: "text.primary",
            },
          ].map((item) => (
            <Paper
              key={item.label}
              variant="outlined"
              sx={{ p: 1.5, textAlign: "center" }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {item.label}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.5, color: item.color }}
              >
                {Number(item.value).toLocaleString("fa-IR")}
              </Typography>
            </Paper>
          ))}
        </Box>
      ) : null}
      {!operatorMode && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<SmsIcon />}
              onClick={() => {
                setNudgePreview(null);
                setNudgeOpen(true);
              }}
            >
              پیامک یادآوری
            </Button>
          }
        >
          می‌توانید برای متقاضیان با پروفایل تکمیل‌شده که هنوز درخواست ثبت
          نکرده‌اند، پیامک یادآوری ارسال کنید. ابتدا پیش‌نمایش بگیرید.
        </Alert>
      )}
      {operatorMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          به‌صورت پیش‌فرض لیدهای ارجاع‌شده به شما نمایش داده می‌شود؛ با فیلتر
          «کارشناس» می‌توانید همه را ببینید.
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="جستجو سرور (نام / موبایل)"
              size="small"
              fullWidth
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(0);
                  setFilterTick((x) => x + 1);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>وضعیت تماس</InputLabel>
              <Select
                label="وضعیت تماس"
                value={filters.contactStatus}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, contactStatus: e.target.value }))
                }
              >
                <MenuItem value="">صف کار (بدون عدم تمایل)</MenuItem>
                {CONTACT_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={String(s.value)}>
                    {s.label}
                    {s.value === 3 ? " — آمار" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>کارشناس</InputLabel>
              <Select
                label="کارشناس"
                value={filters.assignedOperatorUserId}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    assignedOperatorUserId: e.target.value,
                  }))
                }
              >
                <MenuItem value="">همه</MenuItem>
                {operators.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.fullName || u.FullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <PartnerReferralFilterSelect
              value={filters.partnerReferralLevel1PartnerId}
              onChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  partnerReferralLevel1PartnerId: value,
                }))
              }
              rows={rows}
              fetchPartnerReferralFilterOptions={api.getPartnerReferralFilterOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>نیاز پیگیری</InputLabel>
              <Select
                label="نیاز پیگیری"
                value={filters.needsFollowUp}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, needsFollowUp: e.target.value }))
                }
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="1">بله</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <JalaliDatePicker
              label="ثبت‌نام از"
              fullWidth
              size="small"
              value={filters.registeredFrom ? new Date(filters.registeredFrom) : null}
              onChange={(value) =>
                setFilters((f) => ({ ...f, registeredFrom: value || null }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <JalaliDatePicker
              label="ثبت‌نام تا"
              fullWidth
              size="small"
              value={filters.registeredTo ? new Date(filters.registeredTo) : null}
              onChange={(value) =>
                setFilters((f) => ({ ...f, registeredTo: value || null }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  setFilterTick((x) => x + 1);
                }}
              >
                اعمال فیلتر
              </Button>
              <IconButton color="primary" onClick={() => setFilterTick((x) => x + 1)} aria-label="بروزرسانی">
                <SyncIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Card>
        <CardHeader
          title="لیست متقاضیان"
          subheader={`${total.toLocaleString("fa-IR")} مورد`}
        />
        <CardContent sx={{ px: { xs: 0, sm: 2 } }}>
          <EnhancedTable
            rowKey="userId"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            actionsMode="inline"
            loading={loading}
            showFilter
            showIndex
            showPaging
            pageSize={pageSize}
            serverPagination={serverPagination}
            defaultSort={{ orderBy: "registeredAt", order: "desc" }}
            getRowStatus={(row) => (row.needsFollowUp ? "warning" : "")}
            tableMinWidth={980}
          />
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          پیگیری متقاضی
          <IconButton
            onClick={() => setDetailOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    نام
                  </Typography>
                  <Typography fontWeight={600}>{detail.fullName || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    موبایل
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={600}>{detail.mobile || "—"}</Typography>
                    {detail.mobile ? (
                      <Button
                        size="small"
                        startIcon={<PhoneIcon />}
                        href={`tel:${detail.mobile}`}
                      >
                        تماس
                      </Button>
                    ) : null}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    وضعیت پروفایل
                  </Typography>
                  <Typography>{detail.profileStatusTitle || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    آخرین تماس
                  </Typography>
                  <Typography>
                    {detail.lastContactAt
                      ? new Date(detail.lastContactAt).toLocaleString("fa-IR")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    کارشناس تماس‌گیرنده
                  </Typography>
                  <Typography fontWeight={600}>
                    {detail.lastContactedByOperatorName ||
                      detail.LastContactedByOperatorName ||
                      "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    تاریخ ثبت‌نام
                  </Typography>
                  <Typography>
                    {detail.registeredAt
                      ? new Date(detail.registeredAt).toLocaleString("fa-IR")
                      : "—"}
                  </Typography>
                </Grid>
              </Grid>

              {!operatorMode ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>کارشناس مسئول</InputLabel>
                    <Select
                      label="کارشناس مسئول"
                      value={assignedOperatorUserId}
                      onChange={(e) => setAssignedOperatorUserId(e.target.value)}
                    >
                      <MenuItem value="">بدون ارجاع</MenuItem>
                      {operators.map((u) => (
                        <MenuItem key={u.id} value={String(u.id)}>
                          {u.fullName || u.FullName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    onClick={saveAssignedOperator}
                    sx={{ flexShrink: 0, alignSelf: { sm: "center" } }}
                  >
                    ذخیره ارجاع
                  </Button>
                </Stack>
              ) : null}

              <Typography variant="subtitle1" fontWeight={700}>
                ثبت تماس جدید
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>نتیجه تماس</InputLabel>
                <Select
                  label="نتیجه تماس"
                  value={followUpForm.callResult}
                  onChange={(e) => {
                    const value = e.target.value;
                    const preset = CALL_RESULT_PRESETS.find((p) => p.value === value);
                    setFollowUpForm((f) => ({
                      ...f,
                      callResult: value,
                      contactStatus: preset?.contactStatus || f.contactStatus,
                      needsFollowUp:
                        preset?.contactStatus === "3" ? false : f.needsFollowUp,
                      nextFollowUpAt:
                        preset?.contactStatus === "3" ? null : f.nextFollowUpAt,
                    }));
                  }}
                >
                  <MenuItem value="">انتخاب کنید / متن آزاد پایین</MenuItem>
                  {CALL_RESULT_PRESETS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="نتیجه تماس (متن آزاد)"
                fullWidth
                size="small"
                value={
                  CALL_RESULT_PRESETS.some((p) => p.value === followUpForm.callResult)
                    ? ""
                    : followUpForm.callResult
                }
                onChange={(e) =>
                  setFollowUpForm((f) => ({ ...f, callResult: e.target.value }))
                }
                placeholder="در صورت نیاز نتیجه دلخواه بنویسید"
              />
              <TextField
                label="توضیحات تماس *"
                fullWidth
                multiline
                minRows={3}
                value={followUpForm.description}
                onChange={(e) =>
                  setFollowUpForm((f) => ({ ...f, description: e.target.value }))
                }
              />
              <FormControl fullWidth size="small">
                <InputLabel>دلیل عدم ثبت درخواست</InputLabel>
                <Select
                  label="دلیل عدم ثبت درخواست"
                  value={followUpForm.noRequestReasonCategory}
                  onChange={(e) =>
                    setFollowUpForm((f) => ({
                      ...f,
                      noRequestReasonCategory: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">انتخاب کنید</MenuItem>
                  {reasons.map((r) => (
                    <MenuItem key={r.code || r.Code} value={r.code || r.Code}>
                      {r.title || r.Title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="توضیح تکمیلی دلیل"
                fullWidth
                multiline
                minRows={2}
                value={followUpForm.noRequestReasonNotes}
                onChange={(e) =>
                  setFollowUpForm((f) => ({
                    ...f,
                    noRequestReasonNotes: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت بعد از تماس</InputLabel>
                <Select
                  label="وضعیت بعد از تماس"
                  value={followUpForm.contactStatus}
                  onChange={(e) => {
                    const contactStatus = e.target.value;
                    const isNotInterested = contactStatus === "3";
                    setFollowUpForm((f) => ({
                      ...f,
                      contactStatus,
                      callResult:
                        isNotInterested && !f.callResult
                          ? "عدم تمایل"
                          : f.callResult,
                      needsFollowUp: isNotInterested ? false : f.needsFollowUp,
                      nextFollowUpAt: isNotInterested ? null : f.nextFollowUpAt,
                    }));
                  }}
                >
                  {CONTACT_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={String(s.value)}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {followUpForm.contactStatus === "3" ? (
                <Alert severity="warning">
                  با انتخاب «عدم تمایل»، این متقاضی از صف کار حذف می‌شود؛ رکورد
                  برای آمارگیری باقی می‌ماند و کارشناس تماس‌گیرنده ثبت می‌شود.
                </Alert>
              ) : null}
              <JalaliDateTimePicker
                label="پیگیری بعدی"
                fullWidth
                disabled={followUpForm.contactStatus === "3"}
                value={
                  followUpForm.nextFollowUpAt
                    ? new Date(followUpForm.nextFollowUpAt)
                    : null
                }
                onChange={(value) =>
                  setFollowUpForm((f) => ({ ...f, nextFollowUpAt: value || null }))
                }
              />

              {detail.followUps?.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    تاریخچه پیگیری
                  </Typography>
                  <Stack spacing={1}>
                    {detail.followUps.map((fu) => (
                      <Paper key={fu.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {fu.createOn
                            ? new Date(fu.createOn).toLocaleString("fa-IR")
                            : ""}{" "}
                          — کارشناس: {fu.operatorName}
                          {fu.callResult ? ` · ${fu.callResult}` : ""}
                        </Typography>
                        <Typography variant="body2">{fu.description}</Typography>
                        {fu.noRequestReasonCategoryTitle ? (
                          <Typography variant="caption" color="warning.main">
                            دلیل: {fu.noRequestReasonCategoryTitle}
                          </Typography>
                        ) : null}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>بستن</Button>
          <Button variant="contained" onClick={submitFollowUp}>
            ثبت پیگیری
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={nudgeOpen}
        onClose={() => !nudgeLoading && setNudgeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>پیامک یادآوری ثبت درخواست</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              ابتدا «پیش‌نمایش» بگیرید تا تعداد واجدین شرایط مشخص شود؛ سپس در صورت
              تأیید، ارسال واقعی را انجام دهید.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={nudgeForm.profileCompletedOnly}
                  onChange={(e) =>
                    setNudgeForm((f) => ({
                      ...f,
                      profileCompletedOnly: e.target.checked,
                    }))
                  }
                />
              }
              label="فقط پروفایل تکمیل‌شده / تاییدشده"
            />
            <TextField
              label="سقف تعداد در این ارسال"
              type="number"
              size="small"
              fullWidth
              value={nudgeForm.maxCount}
              onChange={(e) =>
                setNudgeForm((f) => ({ ...f, maxCount: e.target.value }))
              }
              helperText="برای ارسال واقعی پیشنهاد می‌شود ابتدا با عدد کوچک (مثلاً ۵۰) تست کنید."
            />
            <TextField
              label="متن سفارشی (اختیاری)"
              multiline
              minRows={3}
              fullWidth
              value={nudgeForm.customMessage}
              onChange={(e) =>
                setNudgeForm((f) => ({ ...f, customMessage: e.target.value }))
              }
              placeholder="خالی بماند تا متن پیش‌فرض سرور استفاده شود"
            />
            {nudgePreview ? (
              <Alert severity="info">
                واجد شرایط:{" "}
                {(
                  nudgePreview.eligibleCount ?? nudgePreview.EligibleCount ?? 0
                ).toLocaleString("fa-IR")}
                {" — "}
                موبایل معتبر در این دسته:{" "}
                {(
                  nudgePreview.sentCount ?? nudgePreview.SentCount ?? 0
                ).toLocaleString("fa-IR")}
                {" — "}
                نامعتبر:{" "}
                {(
                  nudgePreview.skippedInvalidMobile ??
                  nudgePreview.SkippedInvalidMobile ??
                  0
                ).toLocaleString("fa-IR")}
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {nudgePreview.messagePreview ||
                    nudgePreview.MessagePreview ||
                    ""}
                </Typography>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={nudgeLoading} onClick={() => setNudgeOpen(false)}>
            بستن
          </Button>
          <Button
            disabled={nudgeLoading}
            variant="outlined"
            onClick={() => runNudgeSms({ dryRun: true, confirmSend: false })}
          >
            پیش‌نمایش
          </Button>
          <Button
            disabled={nudgeLoading || !nudgePreview}
            color="warning"
            variant="contained"
            onClick={() => {
              if (
                !window.confirm(
                  "ارسال واقعی پیامک به متقاضیان بدون درخواست انجام شود؟"
                )
              ) {
                return;
              }
              runNudgeSms({ dryRun: false, confirmSend: true });
            }}
          >
            ارسال واقعی
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerWithoutRequestLeadsPage;
